import type { CSSProperties, ReactNode } from 'react';
import { cx } from './cx';
import type { VenueSlug } from '../lib/types';
import './components.css';

export interface CardProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Right-hand side of the header — filters, a menu, a freshness badge. */
  actions?: ReactNode;
  footer?: ReactNode;
  children?: ReactNode;
  /** Tints the card with that venue's accent and scopes `--venue` inside it. */
  venue?: VenueSlug | 'company';
  /** Draw the accent border and corner wash. Implied by `venue`. */
  accent?: boolean;
  /** Hover lift + keyboard activation. Provide `onClick` with it. */
  interactive?: boolean;
  onClick?: () => void;
  /** No border, no background — for grouping without another box. */
  flush?: boolean;
  padding?: 'default' | 'tight' | 'none';
  className?: string;
  style?: CSSProperties;
  id?: string;
  /** Announced label when the card itself is the control. */
  ariaLabel?: string;
}

/**
 * The one box. Everything on every screen sits in one of these, so a screen
 * never invents its own panel treatment.
 */
export function Card({
  title, subtitle, actions, footer, children, venue, accent, interactive,
  onClick, flush, padding = 'default', className, style, id, ariaLabel,
}: CardProps) {
  const hasHead = title != null || subtitle != null || actions != null;
  const clickable = Boolean(interactive && onClick);

  return (
    <section
      id={id}
      data-venue={venue}
      className={cx('k-card', (accent || venue) && 'k-card--accent',
        interactive && 'k-card--interactive', flush && 'k-card--flush', className)}
      style={style}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); }
      } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={ariaLabel}
    >
      {hasHead && (
        <header className="k-card__head">
          <div className="k-card__titles">
            {title != null && <div className="k-card__title">{title}</div>}
            {subtitle != null && <div className="k-card__sub">{subtitle}</div>}
          </div>
          {actions != null && <div className="k-card__actions">{actions}</div>}
        </header>
      )}
      {children != null && (
        <div className={cx('k-card__body',
          padding === 'tight' && 'k-card__body--tight',
          padding === 'none' && 'k-card__body--none')}>
          {children}
        </div>
      )}
      {footer != null && <footer className="k-card__foot">{footer}</footer>}
    </section>
  );
}
